// src/pages/Home.jsx
import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Layout,
  Button,
  Typography,
  Row,
  Col,
  Card,
  Space,
  Divider,
  Tag,
  Collapse,
} from "antd";
import {
  LoginOutlined,
  ArrowRightOutlined,
  HeartOutlined,
  CarOutlined,
  TeamOutlined,
  FilePdfOutlined,
  ThunderboltOutlined,
  EuroCircleOutlined,
  CheckOutlined,
  SafetyCertificateOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  ToolOutlined,
  SolutionOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import PublicHeader from "../components/PublicHeader";
import logo from "../assets/logo.png";

const { Content, Footer } = Layout;
const { Title, Paragraph, Text } = Typography;

const COLORS = {
  primary: "#3FB6C2",
  primaryDark: "#1F4E57",
  primaryLight: "#E8F7F9",
  pageBg: "#F4FBFC",
  border: "#DCE7F3",
  text: "#1F2937",
  muted: "#6B7280",
  success: "#52c41a",
};

const featureCards = [
  {
    icon: <CarOutlined />,
    title: "Tourenoptimierung",
    text: "Planen Sie Hin- und Rückfahrten mit mehreren Fahrzeugen, Kapazitäten, Rollstuhlplätzen und Begleitpersonen.",
  },
  {
    icon: <EnvironmentOutlined />,
    title: "Einrichtungen & Zielorte",
    text: "Legen Sie Einrichtungen zentral mit Adresse und eigenen Vorgaben an und ordnen Sie Fahrgäste dem passenden Zielort zu.",
  },
  {
    icon: <ClockCircleOutlined />,
    title: "Flexible Zeitfenster",
    text: "Definieren Sie je Wochentag Zeitfenster für Abholung und Ankunft – als Standard, pro Einrichtung oder individuell je Fahrgast.",
  },
  {
    icon: <SafetyCertificateOutlined />,
    title: "Adressprüfung",
    text: "Adressen werden geocodiert und auf Plausibilität geprüft. Fehlerhafte Einträge können gezielt nachbearbeitet werden.",
  },
  {
    icon: <ThunderboltOutlined />,
    title: "Reale Straßendistanzen",
    text: "Die Planung berücksichtigt echte Strecken statt einfacher Luftlinie – für reale Touren und Fahrzeiten.",
  },
  {
    icon: <FilePdfOutlined />,
    title: "Fahrpläne mit Uhrzeiten",
    text: "Erstellen Sie PDF-Tagesfahrpläne mit berechneten Abhol- und Ankunftszeiten sowie Hinweisen bei Abweichungen.",
  },
  {
    icon: <TeamOutlined />,
    title: "Zentrale Datenverwaltung",
    text: "Verwalten Sie Fahrgäste, Einrichtungen, Fahrzeuge, Depots, Verfügbarkeiten und Kostenfaktoren an einem Ort.",
  },
  {
    icon: <SolutionOutlined />,
    title: "Planungsvorgaben per Excel",
    text: "Importieren und exportieren Sie Standardzeiten und Einrichtungen bequem als Excel-Arbeitsmappe.",
  },
  {
    icon: <BarChartOutlined />,
    title: "Kosten im Blick",
    text: "Kilometer, Fahrzeugeinsatz und Planungsaufwand werden transparent und vergleichbar.",
  },
  {
    icon: <ToolOutlined />,
    title: "Praxisnah entwickelt",
    text: "Die Anwendung ist auf echte Anforderungen von Fahrdiensten, sozialen Trägern und Shuttle-Planung ausgelegt.",
  },
];

const targetGroups = [
  "Soziale Einrichtungen",
  "Werkstätten & Tagesförderstätten",
  "Fahrdienste",
  "Shuttle-Angebote",
  "Betreuungs- und Assistenzdienste",
  "Organisationen mit regelmäßigen Sammelfahrten",
];

const benefits = [
  "Ersparnis manueller Planung in Excel oder auf Papier",
  "Bessere Auslastung vorhandener Fahrzeuge",
  "Abhol- und Ankunftszeiten zuverlässig in die Planung einbeziehen",
  "Einrichtungen und ihre Zeitvorgaben nur einmal zentral pflegen",
  "Übersicht über Rollstuhlplätze, Sitzplätze und Begleitpersonen",
  "Schnelle Reaktion auf Änderungen bei Passagieren oder Fahrzeugen",
  "Klare Tagesfahrpläne für Fahrerinnen und Fahrer",
  "Dokumentation von Fehlern, fehlenden Adressen und Sonderfällen",
];

const steps = [
  {
    title: "1. Daten erfassen",
    text: "Fahrgäste, Einrichtungen, Wohnorte, Fahrzeuge und Depots werden einmalig angelegt und einander zugeordnet.",
  },
  {
    title: "2. Zeitfenster festlegen",
    text: "Erfassen Sie Standardzeiten sowie abweichende Vorgaben je Einrichtung oder Fahrgast für Abholung und Ankunft.",
  },
  {
    title: "3. Touren berechnen",
    text: "Die Anwendung sucht passende Zuordnungen und effiziente Fahrstrecken unter Berücksichtigung Ihrer Kapazitäts- und Zeitvorgaben.",
  },
  {
    title: "4. Fahrplan exportieren",
    text: "Das Ergebnis wird mit konkreten Uhrzeiten als übersichtlicher PDF-Tagesfahrplan ausgegeben.",
  },
];

const faqs = [
  {
    key: "1",
    label: "Für wen ist Smart Routing gedacht?",
    children:
      "Smart Routing richtet sich an Organisationen, die regelmäßig Personen mit Bussen oder Kleintransportern befördern – zum Beispiel soziale Träger, Werkstätten, Fahrdienste oder Shuttle-Angebote.",
  },
  {
    key: "2",
    label: "Können Rollstuhlfahrer und Begleitpersonen berücksichtigt werden?",
    children:
      "Ja. Passagiere können mit besonderen Anforderungen erfasst werden, zum Beispiel Rollstuhlplatz oder Begleitperson. Die Planung berücksichtigt diese Anforderungen bei der Fahrzeugzuordnung.",
  },
  {
    key: "3",
    label: "Wie werden Zeitfenster berücksichtigt?",
    children:
      "Sie können Zeitfenster für die Abholung am Wohnort, die Ankunft an der Einrichtung, die Abholung an der Einrichtung und die Ankunft zu Hause hinterlegen. Standardzeiten lassen sich je Einrichtung oder individuell je Fahrgast überschreiben. Bei der Berechnung können Abweichungen markiert oder die Einhaltung verbindlich vorgegeben werden.",
  },
  {
    key: "4",
    label: "Kann ich mehrere Einrichtungen mit unterschiedlichen Zeiten verwalten?",
    children:
      "Ja. Einrichtungen werden mit Adresse und eigenen Zeitfenstern je Wochentag zentral angelegt. Zugeordnete Fahrgäste übernehmen diese Vorgaben automatisch, sofern für sie keine individuellen Zeiten hinterlegt sind.",
  },
  {
    key: "5",
    label: "Was passiert bei fehlerhaften Adressen?",
    children:
      "Fehlerhafte oder nicht gefundene Adressen sollen nicht unbemerkt in die Planung laufen. Sie werden gesammelt und können separat geprüft werden.",
  },
  {
    key: "6",
    label: "Kann ich Fahrpläne für Fahrer ausgeben?",
    children:
      "Ja. Tagesfahrpläne lassen sich als PDF mit den berechneten Abhol- und Ankunftszeiten exportieren. Nicht einhaltbare Zeitfenster und Abweichungen werden im entsprechenden Planungsmodus kenntlich gemacht.",
  },
];

export default function Home() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Patrick Sauter – Smart Routing";

  }, []);

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <Layout style={{ minHeight: "100vh", background: "#FFFFFF" }}>
      <PublicHeader />

      <Content>
        <section
          style={{
            background: `radial-gradient(900px 460px at 15% -10%, ${COLORS.primaryLight} 0%, transparent 60%),
                         radial-gradient(900px 460px at 110% 0%, #FFFFFF 0%, transparent 55%),
                         linear-gradient(180deg, #FFFFFF 0%, ${COLORS.pageBg} 100%)`,
            padding: "78px 16px 56px",
          }}
        >
          <div style={{ maxWidth: 1120, margin: "0 auto", textAlign: "center" }}>
            <Tag
              style={{
                background: COLORS.primaryLight,
                color: COLORS.primaryDark,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 999,
                padding: "4px 14px",
                fontWeight: 600,
                marginBottom: 16,
              }}
            >
              Tourenplanung für Fahrdienste, soziale Träger und Shuttle-Angebote
            </Tag>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 12,
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                  justifyContent: "center",
                }}
              >
                <img src={logo} alt="Firmenlogo" style={{ height: 48, width: "auto" }} />

                <Title
                  level={1}
                  style={{
                    margin: 0,
                    color: COLORS.text,
                    fontSize: "clamp(30px, 4vw, 48px)",
                    lineHeight: 1.08,
                    fontWeight: 900,
                  }}
                >
                  Patrick Sauter - Smart Routing
                </Title>
              </div>

              <Title
                level={2}
                style={{
                  margin: 0,
                  color: COLORS.text,
                  fontSize: "clamp(28px, 4vw, 44px)",
                  lineHeight: 1.12,
                  fontWeight: 400,
                }}
              >
                Weniger Planungsaufwand. Bessere Touren.
              </Title>
            </div>

            <Paragraph
              style={{
                fontSize: 18,
                color: COLORS.muted,
                maxWidth: 820,
                margin: "0 auto 28px",
              }}
            >
              Smart Routing unterstützt Sie dabei, Personenbeförderung effizienter und kostensparender zu planen:
              mit zentral verwalteten Einrichtungen, flexiblen Zeitfenstern für Abholung
              und Ankunft, Fahrzeugkapazitäten, realen Straßendistanzen, automatischer
              Optimierung und PDF-Fahrplänen.
            </Paragraph>

            <Space size="middle" wrap>
              <Button
                type="primary"
                size="large"
                icon={<LoginOutlined />}
                onClick={() => navigate("/login")}
              >
                Jetzt anmelden
              </Button>

              <Button size="large" icon={<ArrowRightOutlined />} onClick={() => scrollTo("#features")}>
                Funktionen ansehen
              </Button>

              <Button size="large" onClick={() => navigate("/contact")}>
                Angebot anfragen
              </Button>
            </Space>
          </div>
        </section>

        <section style={{ padding: "56px 16px 24px" }}>
          <div style={{ maxWidth: 1120, margin: "0 auto" }}>
            <Row gutter={[18, 18]}>
              {[
                {
                  icon: <ClockCircleOutlined />,
                  title: "Zeit sparen",
                  text: "Lösung auf Knopfdruck: kein manuelles Verschieben, Prüfen und Nachrechnen.",
                },
                {
                  icon: <EuroCircleOutlined />,
                  title: "Kosten im Griff",
                  text: "Durch effizientere Planung sinken Fahrzeiten, Strecken und damit auch Ihre Kosten.",
                },
                {
                  icon: <CarOutlined />,
                  title: "Fahrzeuge besser nutzen",
                  text: "Kapazitäten und Anforderungen werden strukturiert berücksichtigt.",
                },
                {
                  icon: <SolutionOutlined />,
                  title: "Übersicht behalten",
                  text: "Alle wichtigen Stammdaten und Touren an einem zentralen Ort.",
                },
              ].map((item) => (
                <Col xs={24} sm={12} lg={6} key={item.title}>
                  <Card
                    style={{
                      height: "100%",
                      borderRadius: 18,
                      border: `1px solid ${COLORS.border}`,
                      boxShadow: "0 8px 24px rgba(31, 78, 87, 0.06)",
                    }}
                  >
                    <Space direction="vertical" size={10}>
                      <div style={{ fontSize: 28, color: COLORS.primaryDark }}>{item.icon}</div>
                      <Title level={4} style={{ margin: 0, color: COLORS.text }}>
                        {item.title}
                      </Title>
                      <Paragraph style={{ margin: 0, color: COLORS.muted }}>
                        {item.text}
                      </Paragraph>
                    </Space>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </section>

        <section id="features" style={{ padding: "64px 16px", scrollMarginTop: 88 }}>
          <div style={{ maxWidth: 1120, margin: "0 auto" }}>
            <Title level={2} style={{ textAlign: "center", marginBottom: 8, color: COLORS.text }}>
              Funktionen im Überblick
            </Title>

            <Paragraph style={{ textAlign: "center", marginBottom: 34, color: COLORS.muted }}>
              Alles, was Sie für eine strukturierte und realistische Tourenplanung brauchen.
            </Paragraph>

            <Row gutter={[18, 18]}>
              {featureCards.map((item) => (
                <Col xs={24} md={12} lg={6} key={item.title}>
                  <Card
                    hoverable
                    style={{
                      height: "100%",
                      borderRadius: 18,
                      border: `1px solid ${COLORS.border}`,
                      boxShadow: "0 8px 24px rgba(31, 78, 87, 0.06)",
                    }}
                  >
                    <Space direction="vertical" size={10}>
                      <div
                        style={{
                          width: 46,
                          height: 46,
                          borderRadius: 14,
                          background: COLORS.primaryLight,
                          color: COLORS.primaryDark,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 24,
                        }}
                      >
                        {item.icon}
                      </div>

                      <Title level={4} style={{ margin: 0, color: COLORS.text }}>
                        {item.title}
                      </Title>

                      <Paragraph style={{ margin: 0, color: COLORS.muted }}>
                        {item.text}
                      </Paragraph>
                    </Space>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </section>

        <section style={{ padding: "0 16px 64px" }}>
          <div style={{ maxWidth: 1120, margin: "0 auto" }}>
            <Row gutter={[18, 18]}>
              <Col xs={24} md={12}>
                <Card
                  style={{
                    height: "100%",
                    borderRadius: 20,
                    border: `1px solid ${COLORS.border}`,
                    background: COLORS.pageBg,
                  }}
                >
                  <Title level={3} style={{ color: COLORS.text }}>
                    Für wen ist die App geeignet?
                  </Title>

                  <Paragraph style={{ color: COLORS.muted }}>
                    Smart Routing eignet sich für Organisationen, die regelmäßig Personen
                    einsammeln, zu einer oder mehreren Einrichtungen bringen und innerhalb
                    festgelegter Zeitfenster wieder zurückfahren.
                  </Paragraph>

                  <Space wrap>
                    {targetGroups.map((item) => (
                      <Tag key={item}>{item}</Tag>
                    ))}
                  </Space>
                </Card>
              </Col>

              <Col xs={24} md={12}>
                <Card
                  style={{
                    height: "100%",
                    borderRadius: 20,
                    border: `1px solid ${COLORS.border}`,
                  }}
                >
                  <Title level={3} style={{ color: COLORS.text }}>
                    Welchen Nutzen haben Sie?
                  </Title>

                  <Space direction="vertical" size={10}>
                    {benefits.map((item) => (
                      <div key={item} style={{ display: "flex", gap: 8 }}>
                        <CheckOutlined style={{ color: COLORS.success, marginTop: 4 }} />
                        <span>{item}</span>
                      </div>
                    ))}
                  </Space>
                </Card>
              </Col>
            </Row>
          </div>
        </section>

        <section id="how" style={{ padding: "16px 16px 64px", scrollMarginTop: 88 }}>
          <div style={{ maxWidth: 1120, margin: "0 auto" }}>
            <Title level={2} style={{ textAlign: "center", marginBottom: 8, color: COLORS.text }}>
              So funktioniert’s
            </Title>

            <Paragraph style={{ textAlign: "center", marginBottom: 34, color: COLORS.muted }}>
              Von den Stammdaten bis zum fertigen Tagesfahrplan.
            </Paragraph>

            <Row gutter={[18, 18]}>
              {steps.map((step) => (
                <Col xs={24} md={12} lg={6} key={step.title}>
                  <Card
                    hoverable
                    style={{
                      height: "100%",
                      borderRadius: 18,
                      border: `1px solid ${COLORS.border}`,
                    }}
                  >
                    <Title level={4} style={{ marginBottom: 8, color: COLORS.primaryDark }}>
                      {step.title}
                    </Title>

                    <Paragraph style={{ margin: 0, color: COLORS.muted }}>
                      {step.text}
                    </Paragraph>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </section>



        <section id="faq" style={{ padding: "0 16px 64px", scrollMarginTop: 88 }}>
          <div style={{ maxWidth: 900, margin: "0 auto" }}>
            <Title level={2} style={{ textAlign: "center", marginBottom: 24, color: COLORS.text }}>
              Häufige Fragen
            </Title>

            <Collapse
              bordered={false}
              items={faqs}
              style={{
                background: "#FFFFFF",
                border: `1px solid ${COLORS.border}`,
                borderRadius: 18,
              }}
            />
          </div>
        </section>

        <section id="why" style={{ padding: "0 16px 64px" }}>
          <div style={{ maxWidth: 1120, margin: "0 auto" }}>
            <Card
              style={{
                borderRadius: 20,
                border: `1px solid ${COLORS.border}`,
                background: `linear-gradient(135deg, ${COLORS.primaryLight} 0%, #FFFFFF 62%)`,
              }}
            >
              <Row gutter={[18, 18]} align="middle">
                <Col xs={24} md={15}>
                  <Space direction="vertical" size={10}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <HeartOutlined style={{ color: COLORS.primaryDark, fontSize: 28, flexShrink: 0 }} />
                      <Title level={3} style={{ margin: 0, color: COLORS.text }}>
                        Persönlich klären, was zu Ihrem Fahrdienst passt
                      </Title>
                    </div>
                    <Paragraph style={{ margin: 0, color: COLORS.muted }}>
                      Nehmen Sie einfach Kontakt zu mir auf. Gemeinsam besprechen wir
                      Ihre Anforderungen. Ich prüfe individuell, wie Smart Routing
                      zu Ihrer Organisation passt, und berate Sie persönlich.
                    </Paragraph>
                  </Space>
                </Col>

                <Col xs={24} md={9} style={{ textAlign: "center" }}>
                  <Button
                    type="primary"
                    size="large"
                    icon={<ArrowRightOutlined />}
                    onClick={() => navigate("/contact")}
                  >
                    Kontakt aufnehmen
                  </Button>
                </Col>
              </Row>
            </Card>
          </div>
        </section>
      </Content>

      <Footer
        style={{
          background: "#FFFFFF",
          borderTop: `1px solid ${COLORS.border}`,
        }}
      >
        <div style={{ maxWidth: 1120, margin: "0 auto" }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={12}>
              <Text style={{ color: COLORS.muted }}>
                © {new Date().getFullYear()} Patrick Sauter – Smart Routing
              </Text>
              <Divider type="vertical" />
              <Link to="/ueber-mich">Über mich</Link>
              <Divider type="vertical" />
              <Link to="/impressum">Impressum</Link>
              <Divider type="vertical" />
              <Link to="/datenschutz">Datenschutz</Link>
              <Divider type="vertical" />
              <Link to="/contact">Kontakt</Link>
            </Col>

            <Col xs={24} md={12} style={{ textAlign: "right" }}>
              <Space>
                <Link to="/login">Login</Link>
                <Link to="/register">Registrieren</Link>
              </Space>
            </Col>
          </Row>
        </div>
      </Footer>
    </Layout>
  );
}
